import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, AlertTriangle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import Modal from "@/components/app/Modal";
import { useValidatedForm, isBlank } from "@/components/app/FormKit";
import { btnOutline, btnPrimary } from "@/components/app/ui";
import { useActiveStore } from "@/context/ActiveStoreContext";
import {
  BUSINESS_FIELDS, STORE_FIELDS, STORE_DEFAULTS, BusinessFields, StoreFields, ThemePicker, ProductStep, StepProgress, STEP_ICONS,
  validateBusiness, validateStoreDetails, validateProduct, useStepValidator, useMeta, useSlugStatus, useThemes, storePayload,
} from "@/components/onboarding/parts";

const STEPS = [
  { key: "business", label: "Business", icon: STEP_ICONS.business, title: "Your business", fields: BUSINESS_FIELDS },
  { key: "store", label: "Store", icon: STEP_ICONS.store, title: "Your store", fields: STORE_FIELDS },
  { key: "theme", label: "Theme", icon: STEP_ICONS.theme, title: "Choose a theme", fields: [] },
  { key: "product", label: "Product", icon: STEP_ICONS.product, title: "Add your first product", fields: ["name", "price"] },
];

/**
 * "Add New Store" — the registration journey (business → store → theme → first product) run for an
 * already signed-in vendor, so the account step is skipped. Uses the same fields, validation and
 * store-creation logic as signup; nothing is saved until the final step succeeds.
 */
export default function AddStoreModal({ open, onClose }) {
  const { adoptNewStore } = useActiveStore();
  const meta = useMeta();
  const themes = useThemes();
  const [step, setStep] = useState(1);
  const [theme, setTheme] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const [baseline, setBaseline] = useState(null);       // prefilled values, to tell real edits from defaults
  const guard = useRef(false);

  const validate = useStepValidator([validateBusiness, validateStoreDetails, validateProduct]);
  const form = useValidatedForm({ ...STORE_DEFAULTS, name: "", price: "", images: [] }, validate);
  const { values: f } = form;
  const slugStatus = useSlugStatus(f.store_slug);
  const formRef = useRef(form); formRef.current = form;

  // Fresh journey every time it opens; business details are prefilled from the current store (editable).
  useEffect(() => {
    if (!open) return;
    setStep(1); setTheme(null); setError(""); setConfirmClose(false); guard.current = false;
    formRef.current.reset?.();
    api.get("/vendor/store").then(({ data: s }) => {
      const pre = {
        business_name: s.business_name || "", business_category: s.business_category || "", business_type: s.business_type || "Sole Proprietor",
        country: s.country || "India", state: s.state || "", city: s.city || "", address: s.address || "", gst_number: s.gst_number || "",
      };
      Object.entries(pre).forEach(([k, v]) => formRef.current.set(k, v));
      setBaseline({ ...STORE_DEFAULTS, ...pre, name: "", price: "", images: [] });
    }).catch(() => setBaseline({ ...STORE_DEFAULTS, name: "", price: "", images: [] }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = open && baseline && (Object.keys(baseline).some((k) => (k === "images" ? f.images.length > 0 : f[k] !== baseline[k])) || theme !== null);

  const requestClose = () => {
    if (submitting) return;
    if (dirty) setConfirmClose(true); else onClose();
  };

  const cur = STEPS[step - 1];
  const next = () => {
    if (!form.check(cur.fields)) return;
    if (cur.key === "store") {
      if (slugStatus === "checking") return toast.info("Checking store URL availability… try again in a moment.");
      if (slugStatus === false) return toast.error("That store URL is taken — please pick another.");
    }
    setError("");
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (guard.current) return;                         // duplicate-submission guard (double click / Enter)
    if (!form.check(cur.fields)) return;
    guard.current = true; setSubmitting(true); setError("");
    let created = null;
    try {
      const payload = { ...storePayload(f), ...(theme ? { theme } : {}) };
      ({ data: { store: created } } = await api.post("/vendor/stores", payload));
    } catch (err) {
      const msg = apiErr(err.response?.data?.detail);
      setError(msg); toast.error(msg);
      if (/slug|taken/i.test(msg)) setStep(2);
      guard.current = false; setSubmitting(false);     // nothing was created: keep every input, stay on the current store
      return;
    }
    // The store exists. Finish its initial set-up against *that* store explicitly (never the active one).
    const h = { headers: { "X-Store-Id": created.id } };
    let setupWarning = "";
    try {
      if (!isBlank(f.name) && !isBlank(f.price)) {
        await api.post("/vendor/products", {
          name: f.name.trim(), price: parseFloat(f.price), image: f.images[0] || "", images: f.images,
          stock: 10, status: "active", featured: true, category: f.business_category || "General",
        }, h);
      }
    } catch (err) { setupWarning = `Store created, but the first product couldn't be saved (${apiErr(err.response?.data?.detail)}). Add it from Products.`; }
    try { await api.put("/vendor/store", { onboarding_complete: true }, h); } catch { /* cosmetic flag */ }

    const ok = await adoptNewStore(created);
    guard.current = false; setSubmitting(false);
    if (ok) {
      onClose({ created: true });
      toast.success(`“${created.name}” is ready — you're now managing it.`);
      if (setupWarning) toast.warning(setupWarning);
    } else {
      onClose({ created: true });
      toast.warning(`“${created.name}” was created, but we couldn't switch to it. Pick it from the store menu.`);
    }
  };

  const last = step === STEPS.length;
  const footer = (
    <>
      <button type="button" onClick={requestClose} disabled={submitting} data-testid="add-store-cancel" className={`${btnOutline} mr-auto`}>Cancel</button>
      {step > 1 && <button type="button" onClick={() => { setError(""); setStep((s) => s - 1); }} disabled={submitting} data-testid="add-store-back" className={btnOutline}><ArrowLeft size={14} /> Back</button>}
      {!last
        ? <button type="button" onClick={next} data-testid="add-store-next" className={btnPrimary}>Continue <ArrowRight size={14} /></button>
        : <button type="button" onClick={submit} disabled={submitting} data-testid="add-store-submit" className={btnPrimary}>
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Creating store…</> : <>Create store <Check size={14} /></>}
          </button>}
    </>
  );

  return (
    <>
      <Modal open={open} onClose={requestClose} size="xl" testId="add-store-modal" title="Add New Store"
        subtitle={`Step ${step} of ${STEPS.length} · ${cur.title}. Same set-up as your first store — no new account needed.`} footer={footer}>
        <div className="mb-5"><StepProgress steps={STEPS} step={step} /></div>
        {error && (
          <div role="alert" data-testid="add-store-error" className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" /> <span>{error} Your details are still here — fix the problem and try again.</span>
          </div>
        )}
        <fieldset disabled={submitting} className="min-w-0 border-0 p-0 m-0">
          {step === 1 && <div className="grid grid-cols-2 gap-x-3 gap-y-3"><BusinessFields bind={form.bind} meta={meta} tp="addstore" /></div>}
          {step === 2 && <div className="grid grid-cols-2 gap-x-3 gap-y-3"><StoreFields form={form} slugStatus={slugStatus} tp="addstore" /></div>}
          {step === 3 && <ThemePicker themes={themes} category={f.business_category} selected={theme} onPick={setTheme} tp="addstore" />}
          {step === 4 && <ProductStep form={form} tp="addstore" />}
        </fieldset>
      </Modal>

      <Modal open={confirmClose} onClose={() => setConfirmClose(false)} size="md" testId="add-store-discard" title="Discard this new store?"
        footer={<>
          <button type="button" onClick={() => setConfirmClose(false)} data-testid="add-store-keep" className={btnOutline}>Keep editing</button>
          <button type="button" onClick={() => { setConfirmClose(false); onClose(); }} data-testid="add-store-discard-confirm" className="h-9 px-4 rounded-lg bg-red-600 text-white text-[13px] font-semibold hover:bg-red-700">Discard</button>
        </>}>
        <p className="text-[13px] text-slate-600">You haven&apos;t created this store yet. If you close now, everything you entered will be lost.</p>
      </Modal>
    </>
  );
}
